  // src/utils/networkErrorHandler.jsx
  import { useCallback, useEffect, useState } from 'react';
  import ReactDOM from 'react-dom/client'; // Use createRoot instead of render

  // CSS styles
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
    },
    popup: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '400px',
      width: '90%',
      textAlign: 'center',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    },
    icon: {
      fontSize: '48px',
      marginBottom: '16px',
    },
    title: {
      margin: '0 0 12px 0',
      color: '#333',
      fontSize: '20px',
      fontWeight: 'bold',
    },
    message: {
      margin: '0 0 20px 0',
      color: '#666',
      lineHeight: '1.5',
    },
    button: {
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      padding: '10px 24px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'background-color 0.2s',
    },
  };

  // Keyframes animation styles to be injected globally
  const animationStyles = `
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
    
    @keyframes slideUp {
      from {
        transform: translateY(20px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
    
    .network-error-overlay {
      animation: fadeIn 0.3s ease-in-out;
    }
    
    .network-error-popup {
      animation: slideUp 0.3s ease-in-out;
    }
  `;

  // Inject global styles once
  if (typeof document !== 'undefined') {
    const styleTag = document.createElement('style');
    styleTag.textContent = animationStyles;
    document.head.appendChild(styleTag);
  }

  // Proper React component with hooks
  const NetworkErrorPopup = ({ onClose }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isHovered, setIsHovered] = useState(false);

    const handleClose = useCallback(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, [onClose]);

    // This useEffect is fine - it's inside a proper React component
    useEffect(() => {
      // Auto-close after 5 seconds? Optional feature
      const timer = setTimeout(() => {
        if (isVisible) {
          handleClose();
        }
      }, 5000);

      return () => clearTimeout(timer);
    }, [isVisible, handleClose]);

    if (!isVisible) return null;

    return ReactDOM.createPortal(
      <div style={styles.overlay} className="network-error-overlay">
        <div style={styles.popup} className="network-error-popup">
          <div style={styles.icon}>📡</div>
          <h3 style={styles.title}>No Internet Connection</h3>
          <p style={styles.message}>Please check your network connection and try again.</p>
          <button
            onClick={handleClose}
            style={{
              ...styles.button,
              backgroundColor: isHovered ? '#45a049' : '#4CAF50',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            Dismiss
          </button>
        </div>
      </div>,
      document.body
    );
  };

  // Main network error handler using React 18's createRoot
  class NetworkErrorHandler {
    constructor() {
      this.isOnline = navigator.onLine;
      this.root = null;
      this.container = null;
      this.setupListeners();
    }

    setupListeners() {
      window.addEventListener('online', () => {
        this.isOnline = true;
        // Removed popup hiding to let toast handle it
      });

      window.addEventListener('offline', () => {
        this.isOnline = false;
        // Removed popup showing to let toast handle it
      });
    }

    showNetworkPopup() {
      // Disabled - using toast notifications instead
      // if (!this.root && !this.isOnline) {
      //   this.renderPopup();
      // }
    }

    hidePopup() {
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
      if (this.container && this.container.parentNode) {
        this.container.parentNode.removeChild(this.container);
        this.container = null;
      }
    }

    renderPopup() {
      if (this.container) return;
      
      this.container = document.createElement('div');
      this.container.id = 'network-error-container';
      document.body.appendChild(this.container);
      
      this.root = ReactDOM.createRoot(this.container);
      this.root.render(
        <NetworkErrorPopup onClose={() => this.hidePopup()} />
      );
    }

    checkConnection() {
      if (!navigator.onLine) {
        this.showNetworkPopup();
        return false;
      }
      return true;
    }
  }

  // Singleton instance
  const networkErrorHandler = new NetworkErrorHandler();

  // ========== CORRECT HOOKS FOR YOUR COMPONENTS ==========

  // Hook 1: Get network status (to use in your components)
  export const useNetworkStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }, []);

    return isOnline;
  };

  // Hook 2: Get network status with additional info
  export const useNetworkStatusWithDetails = () => {
    const [networkState, setNetworkState] = useState({
      isOnline: navigator.onLine,
      connectionType: null,
      effectiveType: null,
      downlink: null,
    });

    useEffect(() => {
      const updateNetworkState = () => {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        setNetworkState({
          isOnline: navigator.onLine,
          connectionType: connection?.type || null,
          effectiveType: connection?.effectiveType || null,
          downlink: connection?.downlink || null,
        });
      };

      const handleOnline = () => updateNetworkState();
      const handleOffline = () => updateNetworkState();

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      // Listen to connection changes if available
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        connection.addEventListener('change', updateNetworkState);
      }

      updateNetworkState();

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        if (connection) {
          connection.removeEventListener('change', updateNetworkState);
        }
      };
    }, []);

    return networkState;
  };

  // Hook 3: Auto-show popup when offline (already handled globally, but this is for manual control)
  export const useNetworkErrorHandler = () => {
    const isOnline = useNetworkStatus();

    useEffect(() => {
      if (!isOnline) {
        networkErrorHandler.showNetworkPopup();
      } else {
        networkErrorHandler.hidePopup();
      }
    }, [isOnline]);

    return {
      isOnline,
      showPopup: () => networkErrorHandler.showNetworkPopup(),
      hidePopup: () => networkErrorHandler.hidePopup(),
      checkConnection: () => networkErrorHandler.checkConnection(),
    };
  };

  // Helper functions
  export const checkConnection = () => {
    return networkErrorHandler.checkConnection();
  };

  export const fetchWithNetworkCheck = async (url, options = {}) => {
    if (!networkErrorHandler.checkConnection()) {
      throw new Error('No internet connection');
    }
    
    return fetch(url, options);
  };

  export const setupAxiosInterceptor = (axios) => {
    axios.interceptors.request.use((config) => {
      if (!networkErrorHandler.checkConnection()) {
        return Promise.reject(new Error('No internet connection'));
      }
      return config;
    });

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Removed popup showing for Network Error - let toast handle it
        // if (error.message === 'Network Error') {
        //   networkErrorHandler.showNetworkPopup();
        // }
        return Promise.reject(error);
      }
    );
  };

  export default networkErrorHandler;